/**
 * Der **Ort** in der Anwendung — und die Adresszeile, die ihn trägt.
 *
 * Die Oberfläche ist eine SPA, aber sie ist deshalb keine Anwendung ohne
 * Adressen: Wer in den Befunden auf eine Stelle klickt, landet in der
 * Revisionsansicht, und ohne einen Eintrag in der Chronik nähme ihn die
 * Zurück-Taste des Browsers aus der Anwendung heraus statt an die Liste zurück.
 * Also führt jeder Ortswechsel einen Eintrag mit sich.
 *
 * **Zum Ort gehören die Filter.** Eine Liste ist nicht derselbe Ort, wenn ein
 * anderer Ausschnitt darin steht: Wer gefiltert, eine Stelle geöffnet und Zurück
 * gedrückt hat, will seinen Ausschnitt wiedersehen und nicht die Vorbelegung.
 * Darum stehen die Filter vollständig in der Adresse — das macht sie nebenbei zu
 * etwas, das man weitergeben kann. Eine Änderung an einem Filter schreibt dabei
 * **keinen** neuen Chronikeintrag, sondern ersetzt den bestehenden: Sonst müsste
 * man siebenmal Zurück drücken, um eine Liste zu verlassen.
 *
 * Zwei Dinge stehen nicht in der Adresse und reisen stattdessen im Zustand des
 * Chronikeintrags mit, weil sie Beiwerk eines Klicks sind und niemand sie tippt:
 * das **Sprungziel** in einer Revision (Datei, Zeilenbereich) und die **zuletzt
 * geöffnete Zeile**, die die Liste nach der Rückkehr hervorhebt.
 *
 * Was der Browser hergibt, wird geprüft und nicht geglaubt: Der Zustand eines
 * Eintrags kann aus einer älteren Fassung der Anwendung stammen. Die Adresse ist
 * darum die Wahrheit über den Ort, der Zustand nur eine Zugabe.
 *
 * Die Adressen sind englisch buchstabiert wie jeder andere Pfad (ADR-0009).
 */

import type {
  Committer,
  FindingKind,
  FindingOrigin,
  FindingStatus,
  Qualification,
  RevisionState,
  RevisionStreamQuery,
  Severity,
  SiteQuery,
} from "../api/client";
import type { FindingAnchor } from "../views/revision/anchor";

import { useCallback, useEffect, useState } from "react";

import { withBase, withoutBase } from "../base";

/**
 * Die Bereiche der Anwendung — englische Kennungen, deutsche Beschriftung.
 *
 * `version-history` ist ein **Ort ohne Bereich**: Sie hat eine Adresse und einen
 * Chronikeintrag (Story 284), steht aber in keinem Modul der Sidebar — der Einstieg
 * ist das Fassungsmenü an der Logo-Zeile (#135). Ein Eintrag im Menü hieße, sie
 * neben die Arbeitsflächen zu stellen; gelesen wird sie einmal.
 */
export type Area =
  | "revisions"
  | "sites"
  | "inventory"
  | "deviation"
  | "follow-up-marks"
  | "scaffold"
  | "administration"
  | "version-history";

/**
 * Eine geöffnete Revision — und, wenn sie aus den **Befunden** geöffnet wurde,
 * die Stelle, an die sie springen soll.
 *
 * Der Anker steht hier vollständig: Datei, Revision, Zeilenbereich. Er genügt
 * zum Zurückfinden, und mehr gibt es nicht (ADR-0006).
 */
export interface OpenRevision {
  id: number;
  target: FindingAnchor | null;
}

/**
 * Die Filter einer Liste, samt Seite — die Frage an den Server ohne die
 * Seitengröße. Die ist eine Eigenschaft der Ansicht und keine des Ortes.
 */
export type StreamFilter = Omit<RevisionStreamQuery, "perPage">;

/**
 * **Die Qualifizierung bleibt hier bei den dreien** (#185): `"unjudged"` kennt
 * die Abfrage an den Vorrat, die Übersicht bietet es nicht an. Es ist die Frage
 * eines einzelnen Sprungs — des Knopfes in der Revisionsansicht — und kein
 * Zustand, den ein Ort trüge; über die Adresse gesetzt bekäme die Liste einen
 * Filter, den ihre Auswahlliste nicht wieder abwählen kann.
 */
export type SiteFilter = Omit<SiteQuery, "perPage" | "qualification"> & {
  qualification: Qualification | null;
};

/** Die Revisionsliste ohne jeden Filter: alles, was hereinkam. */
export const STREAM_START: StreamFilter = {
  page: 1,
  projectId: null,
  svnIdentity: null,
  reviewRecommended: null,
  state: null,
  submitted: null,
  severity: null,
  hasFindings: null,
  // **Nicht vorbelegt** (ADR-0028): Eine Vorauswahl, die niemand gesetzt hat,
  // ist von einer Sichtgrenze nicht zu unterscheiden.
  committer: null,
};

/**
 * **Vorbelegt sind `offen` und die Anzeigeschwelle** (Spec v1.3, „Der
 * Befundvorrat"; Story 241).
 *
 * Sie stehen hier und nicht im Server: Ein Filter, den niemand gesetzt hat und
 * den auch niemand abwählen kann, versteckte den halben Bestand. Was die
 * Schwelle wegnimmt, sagt die Ansicht darüber mit der Zahl.
 *
 * Zugleich ist das die Bedeutung einer Adresse, die nichts dazu sagt: `/sites`
 * ohne Abfrage ist diese Vorbelegung, und nur was davon abweicht, steht dort.
 */
export const SITE_START: SiteFilter = {
  page: 1,
  projectId: null,
  path: null,
  kind: null,
  severity: null,
  status: "open",
  // Vorbelegt auf „alle" (Spec v1.4, Story 252): Die Qualifizierung ist die
  // zweite Achse und nicht die, nach der aufgeräumt wird — ein bestätigter,
  // offener Befund ist der bestbelegte Arbeitsauftrag im System und darf nicht
  // durch eine Vorbelegung aus dem Bild fallen.
  qualification: null,
  origin: null,
  from: null,
  to: null,
  thresholds: true,
  // **Nicht vorbelegt** (ADR-0028), aus demselben Grund wie im Strom: Der Filter
  // tritt zu den Vorbelegungen dieser Ansicht hinzu, wie sie sind.
  committer: null,
};

/**
 * Wo die Anwendung gerade steht.
 *
 * `area` ist der Bereich, in dem man steht, **auch wenn eine Revision offen
 * ist**: Wer aus den Befunden an eine Stelle springt, bleibt in den Befunden,
 * und die Navigationsleiste hebt sie weiter hervor. Sonst spränge die Leiste bei
 * jedem Klick auf eine Zeile in einen Bereich, den niemand gewählt hat, und
 * „zurück" führte anderswohin, als sie anzeigt.
 *
 * Die Filter beider Listen stehen immer beide da, auch wenn nur eine davon
 * gerade zu sehen ist: Sie überleben damit einen Blick in einen anderen Bereich.
 */
export interface Place {
  area: Area;
  revision: OpenRevision | null;
  stream: StreamFilter;
  sites: SiteFilter;
  /** Die Zeile, die zuletzt geöffnet wurde — die Liste hebt sie hervor. */
  last: string | null;
}

const PATHS: Record<Area, string> = {
  revisions: "/revisions",
  sites: "/sites",
  inventory: "/inventory",
  deviation: "/deviation",
  "follow-up-marks": "/follow-up-marks",
  scaffold: "/scaffold",
  administration: "/administration",
  "version-history": "/version-history",
};

/**
 * Die geschlossenen Mengen, gegen die eine Adresse geprüft wird.
 *
 * Sie stehen als vollständige Abbildung und nicht als Liste da: Kommt ein Wert
 * zum Typ hinzu, ist das hier ein Übersetzungsfehler und kein stiller Ausfall
 * eines Filters.
 */
const AREAS: Record<Area, true> = {
  revisions: true,
  sites: true,
  inventory: true,
  deviation: true,
  "follow-up-marks": true,
  scaffold: true,
  administration: true,
  "version-history": true,
};

const STATES: Record<RevisionState, true> = {
  pending: true,
  in_review: true,
  reviewed: true,
  failed: true,
};

const SEVERITIES: Record<Severity, true> = { hinweis: true, mittel: true, schwer: true };

const KINDS: Record<FindingKind, true> = {
  syntax: true,
  konvention: true,
  "nicht-produktiv": true,
  struktur: true,
  migration: true,
};

const STATUSES: Record<FindingStatus, true> = {
  open: true,
  fixed: true,
  dismissed: true,
  superseded: true,
};

const QUALIFICATIONS: Record<Qualification, true> = {
  confirmed: true,
  rejected: true,
  acknowledged: true,
};

const ORIGINS: Record<FindingOrigin, true> = { agent: true, pattern: true, person: true };

/** Der Anfang: die Revisionsliste, ungefiltert. */
export const START: Place = {
  area: "revisions",
  revision: null,
  stream: STREAM_START,
  sites: SITE_START,
  last: null,
};

/** Ein Bereich, sonst nichts — die Filter beider Listen bleiben, wie sie waren. */
export function areaPlace(area: Area, keep: Place = START): Place {
  return { area, revision: null, stream: keep.stream, sites: keep.sites, last: keep.last };
}

/**
 * Die Adresse zu einem Ort.
 *
 * Eine offene Revision ist eine Revision, wo man auch herkam — deshalb steht sie
 * unter `/revisions/…`. Woher man kam, hängt als `from` daran: Das ist der
 * Bereich, den die Leiste hervorhebt und in den „zurück" führt. Die Filter der
 * Liste stehen dann nicht mit in der Adresse; sie reisen im Chronikeintrag mit
 * und sind nach einem Neuladen ausgerechnet dieser Adresse dahin.
 */
export function urlOf(place: Place): string {
  return withBase(pathOf(place));
}

/**
 * Derselbe Ort ohne Unterpfad. Getrennt, weil die Erkennung unten gegen diese
 * Pfade prüft und der Unterpfad dort schon abgeschnitten ist — er gehört genau
 * einmal davor, nämlich in dem, was der Browser sieht.
 */
function pathOf(place: Place): string {
  if (place.revision !== null) {
    const opened = `${PATHS.revisions}/${place.revision.id}`;

    return place.area === "revisions" ? opened : `${opened}?from=${place.area}`;
  }

  if (place.area === "revisions") {
    return withQuery(PATHS.revisions, streamQuery(place.stream));
  }

  if (place.area === "sites") {
    return withQuery(PATHS.sites, siteQuery(place.sites));
  }

  return PATHS[place.area];
}

/**
 * Der Ort zu einer Adresse. Was sie nicht hergibt, ist der Anfang — eine
 * unbekannte Adresse führt in die Revisionsliste und nicht in eine Fehlerseite:
 * Der Server liefert für jeden Pfad dieselbe Hülle aus, also kommt hier auch
 * Getipptes an. Ein unbrauchbarer Filterwert gilt als nicht gesetzt.
 */
export function placeOf(href: string): Place {
  const url = new URL(href, "http://devtools.invalid");
  const query = url.searchParams;
  const path = withoutBase(url.pathname).replace(/\/+$/, "");

  const opened = /^\/revisions\/(\d+)$/.exec(path);

  if (opened !== null) {
    const from = pick(AREAS, query.get("from")) ?? "revisions";

    return { ...START, area: from, revision: { id: Number(opened[1]), target: null } };
  }

  if (path === PATHS.sites) {
    return { ...START, area: "sites", sites: siteFilterOf(query) };
  }

  const area = pick(AREAS, path.replace(/^\//, ""));

  if (area === null || area === "revisions") {
    return { ...START, stream: streamFilterOf(query) };
  }

  return { ...START, area };
}

function withQuery(path: string, query: URLSearchParams): string {
  const rest = query.toString();

  return rest === "" ? path : `${path}?${rest}`;
}

function streamQuery(filter: StreamFilter): URLSearchParams {
  const query = new URLSearchParams();

  set(query, "project", filter.projectId === null ? null : String(filter.projectId));
  set(query, "identity", filter.svnIdentity);
  set(query, "committer", filter.committer);
  set(query, "recommended", flag(filter.reviewRecommended));
  set(query, "state", filter.state);
  set(query, "submitted", flag(filter.submitted));
  set(query, "severity", filter.severity);
  set(query, "findings", flag(filter.hasFindings));
  set(query, "page", filter.page === 1 ? null : String(filter.page));

  return query;
}

function streamFilterOf(query: URLSearchParams): StreamFilter {
  return {
    page: pageOf(query),
    projectId: numberOf(query.get("project")),
    svnIdentity: textOf(query.get("identity")),
    committer: committerOf(query),
    reviewRecommended: boolOf(query.get("recommended")),
    state: pick(STATES, query.get("state")),
    submitted: boolOf(query.get("submitted")),
    severity: pick(SEVERITIES, query.get("severity")),
    hasFindings: boolOf(query.get("findings")),
  };
}

/**
 * Der Lebenszyklus ist der einzige Filter mit einer Vorbelegung, die nicht
 * „alles" heißt: `offen`. Deshalb steht in der Adresse nicht er, sondern jede
 * Abweichung von ihm — und `all` für „kein Status gefiltert", das sonst nicht von
 * „nichts gesagt" zu unterscheiden wäre. Die Qualifizierung daneben steht auf
 * „alle" und braucht das nicht.
 */
function siteQuery(filter: SiteFilter): URLSearchParams {
  const query = new URLSearchParams();

  set(query, "project", filter.projectId === null ? null : String(filter.projectId));
  set(query, "path", filter.path);
  set(query, "kind", filter.kind);
  set(query, "severity", filter.severity);
  set(query, "status", filter.status === SITE_START.status ? null : (filter.status ?? "all"));
  set(query, "qualification", filter.qualification);
  set(query, "origin", filter.origin);
  set(query, "since", filter.from);
  set(query, "until", filter.to);
  set(query, "committer", filter.committer);
  set(query, "thresholds", filter.thresholds ? null : "0");
  set(query, "page", filter.page === 1 ? null : String(filter.page));

  return query;
}

function siteFilterOf(query: URLSearchParams): SiteFilter {
  const status = query.get("status");

  return {
    page: pageOf(query),
    projectId: numberOf(query.get("project")),
    path: textOf(query.get("path")),
    kind: pick(KINDS, query.get("kind")),
    severity: pick(SEVERITIES, query.get("severity")),
    status: status === null ? SITE_START.status : pick(STATUSES, status),
    qualification: pick(QUALIFICATIONS, query.get("qualification")),
    origin: pick(ORIGINS, query.get("origin")),
    from: textOf(query.get("since")),
    to: textOf(query.get("until")),
    committer: committerOf(query),
    thresholds: query.get("thresholds") !== "0",
  };
}

/**
 * Der Committer aus der Adresse: **nur `me`**.
 *
 * Alles andere ist kein Filter, sondern nichts — wie jeder unbrauchbare
 * Filterwert hier. Eine Personenkennung in der Adresse wäre die Auswahl einer
 * fremden Person, und die gibt es nicht (ADR-0028); der Server weist sie
 * ebenfalls ab.
 */
function committerOf(query: URLSearchParams): Committer | null {
  return query.get("committer") === "me" ? "me" : null;
}

function set(query: URLSearchParams, name: string, value: string | null): void {
  if (value !== null && value !== "") {
    query.set(name, value);
  }
}

function flag(value: boolean | null): string | null {
  return value === null ? null : value ? "1" : "0";
}

function boolOf(value: string | null): boolean | null {
  return value === "1" ? true : value === "0" ? false : null;
}

function numberOf(value: string | null): number | null {
  return value !== null && /^\d+$/.test(value) ? Number(value) : null;
}

function textOf(value: string | null): string | null {
  return value === null || value === "" ? null : value;
}

function pageOf(query: URLSearchParams): number {
  return numberOf(query.get("page")) ?? 1;
}

/** Ein Wert aus einer geschlossenen Menge — oder `null`, wenn er nicht dazugehört. */
function pick<T extends string>(known: Record<T, true>, value: string | null): T | null {
  return value !== null && Object.prototype.hasOwnProperty.call(known, value)
    ? (value as T)
    : null;
}

/**
 * Was neben der Adresse im Chronikeintrag steht: das Sprungziel und die zuletzt
 * geöffnete Zeile. Mehr nicht — alles andere liest sich aus der Adresse, und was
 * sich aus ihr liest, kann hier auch nicht veralten.
 */
interface Extras {
  target: FindingAnchor | null;
  last: string | null;
}

function extrasOf(place: Place): Extras {
  return { target: place.revision?.target ?? null, last: place.last };
}

function withExtras(place: Place, extras: Extras): Place {
  return {
    ...place,
    revision: place.revision === null ? null : { ...place.revision, target: extras.target },
    last: extras.last,
  };
}

/** Was der Browser als Zustand hergibt, geprüft. Unbrauchbares ist einfach nichts. */
function asExtras(state: unknown): Extras {
  if (state === null || typeof state !== "object") {
    return { target: null, last: null };
  }

  const fields = state as Record<string, unknown>;
  const last = fields["last"];

  return {
    target: asAnchor(fields["target"]),
    last: typeof last === "string" ? last : null,
  };
}

function asAnchor(value: unknown): FindingAnchor | null {
  if (value === null || typeof value !== "object") {
    return null;
  }

  const fields = value as Record<string, unknown>;

  if (
    typeof fields["projectId"] !== "number" ||
    typeof fields["path"] !== "string" ||
    typeof fields["revision"] !== "number" ||
    typeof fields["startLine"] !== "number" ||
    typeof fields["endLine"] !== "number"
  ) {
    return null;
  }

  return value as unknown as FindingAnchor;
}

/**
 * Der Ort, an dem die Anwendung steht, und die beiden Wege zum nächsten.
 *
 * `go` schreibt einen Chronikeintrag — ein Ortswechsel, den Zurück rückgängig
 * macht. `replace` ersetzt den bestehenden: für alles, was denselben Ort nur
 * anders zuschneidet, also für jede Änderung an einem Filter.
 *
 * `visit` zählt die Ortswechsel. Die Listen hängen ihren Schlüssel daran: Sie
 * halten ihre Filter selbst und übernehmen die des Ortes beim Aufbauen, also
 * müssen sie neu aufgebaut werden, wenn Zurück einen anderen Ausschnitt bringt —
 * aber gerade nicht, wenn der Anwender selbst am Filter dreht.
 */
export function usePlace(): {
  place: Place;
  visit: number;
  go: (next: Place) => void;
  replace: (next: Place) => void;
} {
  const [place, setPlace] = useState<Place>(() =>
    withExtras(placeOf(window.location.href), asExtras(window.history.state)),
  );
  const [visit, setVisit] = useState(0);

  // Nur beim ersten Rendern; später schreiben `go` und `replace` die Einträge.
  // Der Ort wird hier noch einmal gelesen und nicht mitgeschleppt, damit dieser
  // Effekt von nichts abhängt, was sich ändern kann.
  useEffect(() => {
    const start = withExtras(placeOf(window.location.href), asExtras(window.history.state));

    window.history.replaceState(extrasOf(start), "", urlOf(start));
  }, []);

  useEffect(() => {
    const back = (event: PopStateEvent): void => {
      setPlace(withExtras(placeOf(window.location.href), asExtras(event.state)));
      setVisit((current) => current + 1);
    };

    window.addEventListener("popstate", back);

    return () => window.removeEventListener("popstate", back);
  }, []);

  const go = useCallback((next: Place): void => {
    window.history.pushState(extrasOf(next), "", urlOf(next));
    setPlace(next);
    setVisit((current) => current + 1);
  }, []);

  const replace = useCallback((next: Place): void => {
    window.history.replaceState(extrasOf(next), "", urlOf(next));
    setPlace(next);
  }, []);

  return { place, visit, go, replace };
}
